'use client';

import { c as _c } from "react/compiler-runtime";
import { useLocale, useTranslations } from 'next-intl';
import { type FC, useEffect, useRef, useState } from 'react';
import { useUserBalanceQuery } from '@/entities/User/api/getUserBalance';
import { useUserStore } from '@/entities/User/store/userStoreProvider';
import { Link } from '../../../i18n/navigation';
import { cn } from '../../../libs/utils';
import { AnimatedPointsCounter } from '../../atoms/AnimatedPointsCounter';
import { LogoFigma } from '../../atoms/LogoFigma';
import { NavBgGroup } from '../../atoms/NavBgGroup';
import { PointsVideoAnimation } from '../../atoms/PointsVideoAnimation';
import { StatIcon } from '../../atoms/StatIcon';
import { Navigation } from './Navigation';

// Интерфейсы пропсов компонента
interface HeaderV2Props {
  className?: string;
  onNavigate?: (path: string) => void;
}

// Компонент статистики пользователя
const UserStats: FC<{
  id?: number;
}> = t0 => {
  const $ = _c(33);
  const {
    id
  } = t0;
  const {
    data: userBalance
  } = useUserBalanceQuery(id);
  const previousPoeBalanceRef = useRef(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const animationTimeoutRef = useRef(null);
  let t1;
  if ($[0] !== userBalance?.poeBalance) {
    t1 = () => {
      const currentBalance = userBalance?.poeBalance;
      if (currentBalance === undefined) {
        return;
      }
      const prevBalance = previousPoeBalanceRef.current;
      if (prevBalance === null) {
        previousPoeBalanceRef.current = currentBalance;
        return;
      }
      if (currentBalance !== prevBalance) {
        if (animationTimeoutRef.current) {
          clearTimeout(animationTimeoutRef.current);
        }
        setIsAnimating(true);
        previousPoeBalanceRef.current = currentBalance;
        animationTimeoutRef.current = setTimeout(() => {
          setIsAnimating(false);
          animationTimeoutRef.current = null;
        }, 3000);
      }
      return () => {
        if (animationTimeoutRef.current) {
          clearTimeout(animationTimeoutRef.current);
        }
      };
    };
    $[0] = userBalance?.poeBalance;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = userBalance?.poeBalance;
  let t3;
  if ($[2] !== t2) {
    t3 = [t2];
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  useEffect(t1, t3);
  const t4 = !id ? "/auth" : "/settings/my-wallet/";
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <StatIcon type="poe" className="mobile:size-[18px]" />;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const t6 = userBalance?.coinsBalance || 0;
  let t7;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      decimals: 4,
      floor: false
    };
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] !== t6) {
    t8 = <div className="flex flex-row items-center justify-center gap-1.5 mobile:gap-1">{t5}<AnimatedPointsCounter value={t6} className="font-inter whitespace-nowrap text-center text-[17px] font-medium text-[#e9ecf0] mobile:hidden mobile:text-[18px]" mobileClassName="hidden mobile:block font-inter whitespace-nowrap text-center text-[17px] font-medium text-[#e9ecf0] mobile:text-[18px]" formatOptions={t7} animateIntegerOnly={true} /></div>;
    $[6] = t6;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <StatIcon type="fanTokens" className="mobile:size-[18px]" />;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== isAnimating) {
    t10 = <div className="relative">{t9}<PointsVideoAnimation isActive={isAnimating} className="mobile:size-[18px]" videoUrl="/images/point-video.webm" /></div>;
    $[9] = isAnimating;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  const t11 = userBalance?.poeBalance || 0;
  let t12;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      decimals: 4,
      floor: false
    };
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  let t13;
  if ($[12] !== t11) {
    t13 = <AnimatedPointsCounter value={t11} className="font-inter whitespace-nowrap text-center text-[17px] font-medium text-[#e9ecf0] mobile:hidden mobile:text-[18px]" mobileClassName="hidden mobile:block font-inter whitespace-nowrap text-center text-[17px] font-medium text-[#e9ecf0] mobile:text-[18px]" formatOptions={t12} animateIntegerOnly={true} />;
    $[12] = t11;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  let t14;
  if ($[14] !== t10 || $[15] !== t13) {
    t14 = <div className="relative flex flex-row items-center justify-center gap-1 mobile:gap-1">{t10}{t13}</div>;
    $[14] = t10;
    $[15] = t13;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  let t15;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <StatIcon type="crystals" className="mobile:size-[18px]" />;
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  const t16 = userBalance?.totalTokens || 0;
  let t17;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      decimals: 5,
      floor: false
    };
    $[18] = t17;
  } else {
    t17 = $[18];
  }
  let t18;
  if ($[19] !== t16) {
    t18 = <div className="hidden flex-row items-center justify-center gap-1 md:flex mobile:gap-1">{t15}<AnimatedPointsCounter value={t16} className="font-inter whitespace-nowrap text-center text-[17px] font-medium text-[#e9ecf0] mobile:hidden mobile:text-[18px]" mobileClassName="hidden mobile:block font-inter whitespace-nowrap text-center text-[17px] font-medium text-[#e9ecf0] mobile:text-[18px]" formatOptions={t17} /></div>;
    $[19] = t16;
    $[20] = t18;
  } else {
    t18 = $[20];
  }
  let t19;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <StatIcon type="crystals" className="mobile:size-[18px]" />;
    $[21] = t19;
  } else {
    t19 = $[21];
  }
  const t20 = userBalance?.totalTokens || 0;
  let t21;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = {
      decimals: 5,
      floor: false,
      hideZero: false
    };
    $[22] = t21;
  } else {
    t21 = $[22];
  }
  let t22;
  if ($[23] !== t20) {
    t22 = <div className="flex flex-row items-center justify-center gap-1 md:hidden mobile:gap-1">{t19}<AnimatedPointsCounter value={t20} className="font-inter whitespace-nowrap text-center text-[17px] font-medium text-[#e9ecf0] mobile:hidden mobile:text-[18px]" mobileClassName="hidden mobile:block font-inter whitespace-nowrap text-center text-[17px] font-medium text-[#e9ecf0] mobile:text-[18px]" formatOptions={t21} /></div>;
    $[23] = t20;
    $[24] = t22;
  } else {
    t22 = $[24];
  }
  let t23;
  if ($[25] !== t14 || $[26] !== t18 || $[27] !== t22 || $[28] !== t8) {
    t23 = <div className="flex min-w-[230px] flex-row items-center justify-around gap-6 px-2.5 mobile:gap-2.5">{t8}{t14}{t18}{t22}</div>;
    $[25] = t14;
    $[26] = t18;
    $[27] = t22;
    $[28] = t8;
    $[29] = t23;
  } else {
    t23 = $[29];
  }
  let t24;
  if ($[30] !== t23 || $[31] !== t4) {
    t24 = <Link href={t4} className="flex-1">{t23}</Link>;
    $[30] = t23;
    $[31] = t4;
    $[32] = t24;
  } else {
    t24 = $[32];
  }
  return t24;
};

// Основной компонент HeaderV2
export const HeaderV2: FC<HeaderV2Props> = t0 => {
  const $ = _c(66);
  const {
    className,
    onNavigate
  } = t0;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const user = useUserStore(_temp);
  const t = useTranslations("HeaderV2.Button");
  const menuRef = useRef(null);
  const toggleBtnRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== isMobileMenuOpen) {
    t1 = () => {
      if (isMobileMenuOpen) {
        const scrollY = window.scrollY;
        document.body.style.position = "fixed";
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = "100%";
        document.body.style.overflow = "hidden";
      } else {
        const scrollY_0 = document.body.style.top;
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.width = "";
        document.body.style.overflow = "";
        if (scrollY_0) {
          window.scrollTo(0, parseInt(scrollY_0 || "0") * -1);
        }
      }
    };
    t2 = [isMobileMenuOpen];
    $[0] = isMobileMenuOpen;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== isMobileMenuOpen) {
    t3 = () => {
      const handleClickOutside = event => {
        if (toggleBtnRef.current?.contains(event.target as Node)) {
          return;
        }
        if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
          setIsMobileMenuOpen(false);
        }
      };
      if (isMobileMenuOpen) {
        document.addEventListener("mousedown", handleClickOutside);
      }
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    };
    t4 = [isMobileMenuOpen];
    $[3] = isMobileMenuOpen;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] !== isMobileMenuOpen) {
    t5 = () => {
      setIsMobileMenuOpen(!isMobileMenuOpen);
    };
    $[6] = isMobileMenuOpen;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const handleMenuToggle = t5;
  let t6;
  if ($[8] !== onNavigate) {
    t6 = path => {
      onNavigate?.(path);
      setIsMobileMenuOpen(false);
    };
    $[8] = onNavigate;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const handleNavigate = t6;
  let t7;
  if ($[10] !== className) {
    t7 = cn("fixed left-0 right-0 top-0 z-[9999] w-full bg-[#000000]", "hidden mobile:flex", className);
    $[10] = className;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="flex size-10 items-center justify-center"><Link href="/"><LogoFigma size="md" className="outline-none" /></Link></div>;
    t9 = <div className="h-8 w-px bg-gradient-to-b from-[#00000059] via-[#ffffff59] to-[#00000059]" />;
    $[12] = t8;
    $[13] = t9;
  } else {
    t8 = $[12];
    t9 = $[13];
  }
  const t10 = user?.id;
  let t11;
  if ($[14] !== t10) {
    t11 = <div className="flex flex-1 items-center justify-center"><UserStats id={t10} /></div>;
    $[14] = t10;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="h-8 w-px bg-gradient-to-b from-[#00000059] via-[#ffffff59] to-[#00000059]" />;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== isMobileMenuOpen) {
    t13 = <div className="flex size-7 items-center justify-center">{isMobileMenuOpen ? <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-white"><path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg> : <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-white"><path d="M3 12h18M3 6h18M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>}</div>;
    $[17] = isMobileMenuOpen;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== handleMenuToggle || $[20] !== t13) {
    t14 = <button type="button" ref={toggleBtnRef} onClick={handleMenuToggle} className="flex size-8 items-center justify-center">{t13}</button>;
    $[19] = handleMenuToggle;
    $[20] = t13;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== t11 || $[23] !== t14) {
    t15 = <div className="flex h-10 w-full items-center gap-[5px] px-[5px]">{t8}{t9}{t11}{t12}{t14}</div>;
    $[22] = t11;
    $[23] = t14;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t15 || $[26] !== t7) {
    t16 = <header className={t7}>{t15}</header>;
    $[25] = t15;
    $[26] = t7;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== className) {
    t17 = cn("fixed left-0 right-0 top-0 z-50 w-full bg-gradient-to-r from-[#0F0F25] to-[#251E75] bg-cover bg-center bg-no-repeat backdrop-blur-[25px]", "mobile:hidden", className);
    $[28] = className;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <div className="h-10 w-[220px] italic leading-10 text-white" />;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <div className="absolute inset-0"><NavBgGroup className="h-full w-full" /></div>;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  let t20;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <div className="absolute left-[52px] top-[10px] z-10 h-[28.438px] w-[32.5px]"><Link href="/"><LogoFigma size="lg" className="outline-none" /></Link></div>;
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  const t21 = user?.id;
  let t22;
  if ($[33] !== t21) {
    t22 = <div className="flex flex-col items-center gap-2.5"><div className="relative h-[52px] w-[486px]">{t19}{t20}<div className="absolute left-[140px] top-[14px] z-10 w-[275px]"><UserStats id={t21} /></div></div></div>;
    $[33] = t21;
    $[34] = t22;
  } else {
    t22 = $[34];
  }
  const t23 = user?.id ? "/settings/my-wallet" : "/auth";
  let t24;
  if ($[35] !== t || $[36] !== user?.id) {
    t24 = user?.id ? t("MyAccount") : t("Login");
    $[35] = t;
    $[36] = user?.id;
    $[37] = t24;
  } else {
    t24 = $[37];
  }
  let t25;
  if ($[38] !== t23 || $[39] !== t24) {
    t25 = <div className="flex w-[220px] items-end justify-end gap-8"><Link href={t23} className="mt-2 rounded-[32px] border border-[#504a8c] px-4 py-2 font-montserrat text-[14px] font-normal uppercase text-white transition-colors hover:bg-[#504a8c]/20 hover:text-white">{t24}</Link></div>;
    $[38] = t23;
    $[39] = t24;
    $[40] = t25;
  } else {
    t25 = $[40];
  }
  let t26;
  if ($[41] !== t22 || $[42] !== t25) {
    t26 = <div className="flex w-full items-center justify-between">{t18}{t22}{t25}</div>;
    $[41] = t22;
    $[42] = t25;
    $[43] = t26;
  } else {
    t26 = $[43];
  }
  let t27;
  if ($[44] !== handleNavigate) {
    t27 = <div className="w-[917px] px-0 py-4"><Navigation mobileMenuVisible={false} onNavigate={handleNavigate} /></div>;
    $[44] = handleNavigate;
    $[45] = t27;
  } else {
    t27 = $[45];
  }
  let t28;
  if ($[46] !== t26 || $[47] !== t27) {
    t28 = <div className="mx-auto flex max-w-[1272px] flex-col items-center">{t26}{t27}</div>;
    $[46] = t26;
    $[47] = t27;
    $[48] = t28;
  } else {
    t28 = $[48];
  }
  let t29;
  if ($[49] !== t17 || $[50] !== t28) {
    t29 = <header className={t17}>{t28}</header>;
    $[49] = t17;
    $[50] = t28;
    $[51] = t29;
  } else {
    t29 = $[51];
  }
  const t30 = isMobileMenuOpen ? "pointer-events-auto translate-y-0" : "pointer-events-none -translate-y-full";
  let t31;
  if ($[52] !== t30) {
    t31 = cn("fixed left-0 top-8 hidden w-full mobile:block", "z-[9998] transition-transform duration-300", "bottom-0", t30);
    $[52] = t30;
    $[53] = t31;
  } else {
    t31 = $[53];
  }
  let t32;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = {
      background: "#000"
    };
    $[54] = t32;
  } else {
    t32 = $[54];
  }
  let t33;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = () => {
      setIsMobileMenuOpen(false);
    };
    $[55] = t33;
  } else {
    t33 = $[55];
  }
  let t34;
  if ($[56] !== handleNavigate || $[57] !== isMobileMenuOpen) {
    t34 = <div className="flex h-full flex-col items-center justify-center gap-4" onClick={t33}><Navigation mobileMenuVisible={isMobileMenuOpen} onNavigate={handleNavigate} setIsMobileMenuOpen={setIsMobileMenuOpen} /></div>;
    $[56] = handleNavigate;
    $[57] = isMobileMenuOpen;
    $[58] = t34;
  } else {
    t34 = $[58];
  }
  let t35;
  if ($[59] !== t31 || $[60] !== t34) {
    t35 = <div ref={menuRef} className={t31} style={t32}>{t34}</div>;
    $[59] = t31;
    $[60] = t34;
    $[61] = t35;
  } else {
    t35 = $[61];
  }
  let t36;
  if ($[62] !== t16 || $[63] !== t29 || $[64] !== t35) {
    t36 = <>{t16}{t29}{t35}</>;
    $[62] = t16;
    $[63] = t29;
    $[64] = t35;
    $[65] = t36;
  } else {
    t36 = $[65];
  }
  return t36;
};

// Экспортируем типы для использования в других компонентах
export type { HeaderV2Props };
function _temp(store) {
  return store.user;
}